import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as SuspendIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

interface CompanyRegistration {
  id: number;
  company_name: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  industry: string;
  company_size: string;
  website?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_country: string;
  address_postal_code?: string;
  subscription_plan: string;
  subdomain: string;
  status: "pending" | "approved" | "rejected" | "suspended";
  email_verified: boolean;
  admin_approved: boolean;
  admin_approved_by?: number;
  admin_approved_at?: string;
  trial_is_active: boolean;
  trial_start_date: string;
  trial_end_date: string;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  registrations: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    suspended: number;
  };
  recent_registrations: CompanyRegistration[];
  tenants: {
    total_active_tenants: number;
  };
}

export const CompanyRegistrationManagement: React.FC = () => {
  const [registrations, setRegistrations] = useState<CompanyRegistration[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<CompanyRegistration | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState<
    "approve" | "reject" | "suspend"
  >("approve");
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/admin/company-registrations", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setRegistrations(data.data);
      }
    } catch (error) {
      console.error("Failed to load registrations:", error);
      setError("Kayıtlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetch("/api/admin/dashboard-stats", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedRegistration) return;

    try {
      setActionLoading(true);
      const response = await fetch(
        `/api/admin/company-registrations/${selectedRegistration.id}/${actionType}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: actionReason }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setSuccess(
          `${actionType === "approve" ? "Onaylama" : actionType === "reject" ? "Reddetme" : "Askıya alma"} başarılı`
        );
        setActionDialog(false);
        setSelectedRegistration(null);
        setActionReason("");
        loadRegistrations();
        loadStats();
      } else {
        setError(data.error || "İşlem sırasında hata oluştu");
      }
    } catch (error) {
      console.error("Action failed:", error);
      setError("İşlem sırasında hata oluştu");
    } finally {
      setActionLoading(false);
    }
  };

  const openActionDialog = (
    registration: CompanyRegistration,
    type: "approve" | "reject" | "suspend"
  ) => {
    setSelectedRegistration(registration);
    setActionType(type);
    setActionDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "suspended":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Beklemede";
      case "approved":
        return "Onaylandı";
      case "rejected":
        return "Reddedildi";
      case "suspended":
        return "Askıya Alındı";
      default:
        return status;
    }
  };

  useEffect(() => {
    loadRegistrations();
    loadStats();
  }, []);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h2">
          Şirket Kayıt Yönetimi
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadRegistrations();
            loadStats();
          }}
          disabled={loading || statsLoading}
        >
          Yenile
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Toplam Kayıt
                </Typography>
                <Typography variant="h4">
                  {stats.registrations.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Bekleyen
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.registrations.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Onaylanan
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.registrations.approved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Aktif Tenant
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {stats.tenants.total_active_tenants}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Registrations Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Şirket Adı</TableCell>
                <TableCell>İletişim Kişisi</TableCell>
                <TableCell>Subdomain</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Email Doğrulama</TableCell>
                <TableCell>Kayıt Tarihi</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : registrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Henüz kayıt bulunmuyor
                  </TableCell>
                </TableRow>
              ) : (
                registrations.map((registration) => (
                  <TableRow key={registration.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {registration.company_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {registration.industry}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {registration.contact_person_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {registration.contact_person_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {registration.subdomain}.orderapp.com
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={registration.subscription_plan}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(registration.status)}
                        color={getStatusColor(registration.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          registration.email_verified
                            ? "Doğrulandı"
                            : "Beklemede"
                        }
                        color={
                          registration.email_verified ? "success" : "warning"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(registration.created_at).toLocaleDateString(
                          "tr-TR"
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRegistration(registration);
                          setDetailDialog(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                      {registration.status === "pending" && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() =>
                              openActionDialog(registration, "approve")
                            }
                          >
                            <ApproveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              openActionDialog(registration, "reject")
                            }
                          >
                            <RejectIcon />
                          </IconButton>
                        </>
                      )}
                      {registration.status === "approved" && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() =>
                            openActionDialog(registration, "suspend")
                          }
                        >
                          <SuspendIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Şirket Kayıt Detayı
          {selectedRegistration && (
            <Typography variant="subtitle2" color="textSecondary">
              {selectedRegistration.company_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRegistration && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Şirket Bilgileri
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <BusinessIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Şirket Adı"
                        secondary={selectedRegistration.company_name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LanguageIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Subdomain"
                        secondary={`${selectedRegistration.subdomain}.orderapp.com`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Plan"
                        secondary={selectedRegistration.subscription_plan}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Endüstri"
                        secondary={selectedRegistration.industry}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    İletişim Bilgileri
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="İletişim Kişisi"
                        secondary={selectedRegistration.contact_person_name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={selectedRegistration.contact_person_email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Telefon"
                        secondary={selectedRegistration.contact_person_phone}
                      />
                    </ListItem>
                  </List>
                </Grid>
                {selectedRegistration.address_street && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Adres Bilgileri
                    </Typography>
                    <Typography variant="body2">
                      {selectedRegistration.address_street}
                      {selectedRegistration.address_city &&
                        `, ${selectedRegistration.address_city}`}
                      {selectedRegistration.address_state &&
                        `, ${selectedRegistration.address_state}`}
                      {selectedRegistration.address_country &&
                        `, ${selectedRegistration.address_country}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "approve"
            ? "Şirketi Onayla"
            : actionType === "reject"
              ? "Şirketi Reddet"
              : "Şirketi Askıya Al"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {actionType === "approve" &&
              "Bu şirketi onaylamak istediğinizden emin misiniz? Onaylandığında tenant oluşturulacak ve admin kullanıcısı oluşturulacak."}
            {actionType === "reject" &&
              "Bu şirketi reddetmek istediğinizden emin misiniz?"}
            {actionType === "suspend" &&
              "Bu şirketi askıya almak istediğinizden emin misiniz?"}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Not (Opsiyonel)"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="İşlem nedeni..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>İptal</Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={
              actionType === "approve"
                ? "success"
                : actionType === "reject"
                  ? "error"
                  : "warning"
            }
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : actionType === "approve" ? (
              "Onayla"
            ) : actionType === "reject" ? (
              "Reddet"
            ) : (
              "Askıya Al"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
